/**
 * Stock intakes — the list of goods received.
 *
 * Replaces the legacy StockView, which listed ingredient names and purchase
 * price-lists that affected nothing. A row here is a document: a supplier, a
 * date, an invoice and lines that moved real stock when it was posted.
 *
 * GRN additions (docs/feature-stock-grn-variants.md §5.2): a search box over
 * invoice no, delivery note no and supplier, a paperclip on documents with
 * attachments, and a "Received directly" filter for Receive-now documents.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.1.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Actions} from '../../../internals/app/Actions';
import type {
  IntakeStatus,
  StockIntake,
} from '../../../internals/repository/StockIntakeRepository';
import type {Supplier} from '../../../internals/repository/SupplierRepository';
import {receivedDirectly} from '../intakeEditor';

const STATUS_STYLE: Record<IntakeStatus, string> = {
  draft: 'bg-amber-50 text-amber-800 border-amber-200',
  posted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  void: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_LABEL: Record<IntakeStatus, string> = {
  draft: 'Draft',
  posted: 'Posted',
  void: 'Void',
};

const fmt = (n: number, currency?: string) =>
  `${currency || ''} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();

const StockIntakeListView = (props: any) => {
  const {
    intakes,
    suppliers,
    selectedShop,
    stockError,
    fetchIntakes,
    fetchSuppliers,
    postIntake,
    voidIntake,
    deleteIntake,
  } = props;

  const shopId = selectedShop?.shopId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<IntakeStatus | 'all'>('all');
  const [supplierId, setSupplierId] = useState(searchParams.get('supplierId') ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  /** Debounced copy of `search`: the server is asked once typing pauses. */
  const [q, setQ] = useState('');
  const [directOnly, setDirectOnly] = useState(false);
  const [voiding, setVoiding] = useState<StockIntake | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<StockIntake | null>(null);

  const load = React.useCallback(() => {
    if (!shopId) return;
    fetchIntakes(shopId, {
      ...(status !== 'all' ? {status} : {}),
      ...(supplierId ? {supplierId} : {}),
      // The server's date filter wants a full ISO range; a date input gives
      // a day, so widen it to cover that whole day.
      ...(from ? {from: `${from}T00:00:00.000Z`} : {}),
      ...(to ? {to: `${to}T23:59:59.999Z`} : {}),
      ...(q ? {q} : {}),
      limit: 100,
    });
  }, [shopId, status, supplierId, from, to, q, fetchIntakes]);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (shopId) fetchSuppliers(shopId);
  }, [shopId, fetchSuppliers]);

  /** Keep the supplier filter in the URL so a link from Suppliers survives a reload. */
  useEffect(() => {
    if (supplierId) setSearchParams({supplierId}, {replace: true});
    else if (searchParams.get('supplierId')) setSearchParams({}, {replace: true});
  }, [supplierId, setSearchParams, searchParams]);

  const rows = useMemo(() => {
    const all = (intakes ?? []) as StockIntake[];
    return directOnly ? all.filter(receivedDirectly) : all;
  }, [intakes, directOnly]);
  const currency = selectedShop?.currency;

  const newIntake = () => {
    // A brand-new document needs at least one line to be valid server-side,
    // so the editor is where lines are added — create it empty-but-dated and
    // let the editor save the first real version.
    navigate('/stock/intake/new');
  };

  return (
    <div className="h-screen flex flex-col w-full p-6">
      <div className="flex-shrink-0 pb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-text1">Stock intakes</h1>
          <p className="text-sm text-text2 mt-1">
            Goods received. Posting a draft adds the stock; correcting a posted
            document means voiding it and entering a new one.
          </p>
        </div>
        <button
          type="button"
          onClick={newIntake}
          className="nl-btn nl-btn--primary"
        >
          New intake
        </button>
      </div>

      {stockError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          {stockError}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 pb-4">
        <div className="min-w-[220px] flex-1">
          <span className="nl-label">Search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Invoice, delivery note or supplier…"
            className="nl-input"
          />
        </div>
        <div>
          <span className="nl-label">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as IntakeStatus | 'all')}
            className="nl-select w-32"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
            <option value="void">Void</option>
          </select>
        </div>
        <div>
          <span className="nl-label">Supplier</span>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="nl-select w-48"
          >
            <option value="">All suppliers</option>
            {((suppliers ?? []) as Supplier[]).map((s) => (
              <option key={s.supplierId} value={s.supplierId}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="nl-label">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="nl-input w-40"
          />
        </div>
        <div>
          <span className="nl-label">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="nl-input w-40"
          />
        </div>
        <label className="flex h-10 cursor-pointer select-none items-center gap-2 rounded-[10px] border border-borderc2 bg-surface px-3 text-sm text-text2">
          <input
            type="checkbox"
            className="nl-check"
            checked={directOnly}
            onChange={(e) => setDirectOnly(e.target.checked)}
          />
          Received directly
        </label>
        {(status !== 'all' || supplierId || from || to || search || directOnly) && (
          <button
            type="button"
            onClick={() => {
              setStatus('all');
              setSupplierId('');
              setFrom('');
              setTo('');
              setSearch('');
              setDirectOnly(false);
            }}
            className="nl-btn nl-btn--secondary"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-borderc bg-surface">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface2 text-text2">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Received</th>
              <th className="px-4 py-3 text-left font-medium">Supplier</th>
              <th className="px-4 py-3 text-left font-medium">Invoice</th>
              <th className="px-4 py-3 text-right font-medium">Lines</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text2">
                  {q || directOnly || status !== 'all' || supplierId || from || to
                    ? 'Nothing matches these filters.'
                    : <>No intakes yet. &ldquo;New intake&rdquo; records a delivery.</>}
                </td>
              </tr>
            )}
            {rows.map((intake) => (
              <tr key={intake.intakeId} className="border-t border-borderc">
                <td className="px-4 py-3 text-text1">
                  {new Date(intake.receivedAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-text2">
                  {intake.supplierName || '—'}
                  {intake.legacy && (
                    <span className="ml-2 rounded-full bg-surface2 px-2 py-0.5 text-[11px] text-text2">
                      Legacy
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-text2">
                  <span className="flex items-center gap-1.5">
                    {intake.invoiceNo || '—'}
                    {(intake.attachments?.length ?? 0) > 0 && (
                      <span
                        title={`${intake.attachments!.length} attachment(s)`}
                        aria-label="Has attachments"
                      >
                        📎
                      </span>
                    )}
                  </span>
                  {intake.deliveryNoteNo && (
                    <span className="block text-[11px] text-text3">DN {intake.deliveryNoteNo}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-text2">
                  {intake.lines?.length ?? 0}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-text1">
                  {fmt(intake.total, currency)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                      STATUS_STYLE[intake.status]
                    }`}
                  >
                    {STATUS_LABEL[intake.status]}
                  </span>
                  {receivedDirectly(intake) && (
                    <span className="ml-2 text-[11px] text-text3">Received directly</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/stock/intake/${intake.intakeId}`)}
                      className="nl-btn nl-btn--secondary nl-btn--sm"
                    >
                      Open
                    </button>
                    {intake.status === 'draft' && (
                      <>
                        <button
                          type="button"
                          onClick={() => postIntake(shopId, intake.intakeId)}
                          className="nl-btn nl-btn--primary nl-btn--sm"
                        >
                          Post
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(intake)}
                          className="nl-btn nl-btn--secondary nl-btn--sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {intake.status === 'posted' && (
                      <button
                        type="button"
                        onClick={() => {
                          setVoiding(intake);
                          setVoidReason('');
                        }}
                        className="nl-btn nl-btn--secondary nl-btn--sm"
                      >
                        Void
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {voiding && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-md p-5">
            <h2 className="text-base font-semibold text-text1">Void this intake?</h2>
            <p className="mt-2 text-sm text-text2">
              This appends a reversal for every line, so the stock it added
              comes back off. The document stays, marked Void.
            </p>
            <div className="mt-4">
              <span className="nl-label">Reason *</span>
              <input
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Wrong delivery, returned to supplier…"
                className="nl-input"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setVoiding(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!voidReason.trim()}
                onClick={() => {
                  voidIntake(shopId, voiding.intakeId, voidReason.trim());
                  setVoiding(null);
                }}
                className="nl-btn nl-btn--primary"
              >
                Void intake
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-sm p-5">
            <h2 className="text-base font-semibold text-text1">Delete this draft?</h2>
            <p className="mt-2 text-sm text-text2">
              Nothing was posted from it, so no stock changes. This cannot be
              undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteIntake(shopId, confirmDelete.intakeId);
                  setConfirmDelete(null);
                }}
                className="nl-btn nl-btn--danger"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(
  (state: any) => ({
    intakes: state.stock.get('intakes'),
    suppliers: state.stock.get('suppliers'),
    stockError: state.stock.get('stockError'),
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {
    fetchIntakes: Actions.stock.fetchIntakes,
    fetchSuppliers: Actions.stock.fetchSuppliers,
    postIntake: Actions.stock.postIntake,
    voidIntake: Actions.stock.voidIntake,
    deleteIntake: Actions.stock.deleteIntake,
  },
)(StockIntakeListView);
